// src/components/looker/LookerPrintHeader.tsx
/**
 * What a printed /looker page says about itself: whose data, which template,
 * which filters, how fresh, and when it was exported. Hidden on screen, where
 * the control band already says all of it.
 *
 * @module components/looker/LookerPrintHeader
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import type { LookerDashboardPayload } from '@/services/looker/lookerTypes';

interface Props {
  payload: LookerDashboardPayload;
  /** The workspace's name, or the "all workspaces" label. */
  scopeLabel: string;
}

const exportedAt = (): string =>
  new Intl.DateTimeFormat('en-GB', { timeZone: 'Asia/Bangkok', dateStyle: 'medium', timeStyle: 'short' }).format(new Date());

export const LookerPrintHeader: React.FC<Props> = ({ payload, scopeLabel }) => {
  const { t } = useTranslation();
  return (
    <header data-looker-print-header className="hidden print:block border-b-2 border-ink-950 pb-3">
      <p className="text-xs font-semibold uppercase tracking-wide text-neutral-800">{scopeLabel}</p>
      <h2 className="text-2xl font-bold text-ink-950">{payload.template.title}</h2>
      <p className="mt-1 text-sm text-neutral-800">
        {payload.template.filters.map((f) => `${f.title}: ${f.value || t('looker.filterAny')}`).join(' · ')}
      </p>
      <p className="text-sm text-neutral-800">
        {payload.currency && `${t('looker.currency.csvLine', { code: payload.currency })} · `}
        {payload.freshness.latestDataDate && `${t('looker.dataAsOf', { date: payload.freshness.latestDataDate })} · `}
        {t('looker.print.exportedAt', { date: exportedAt() })}
      </p>
    </header>
  );
};
